import React, { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useContent } from '../context/ContentContext';
import { PageBanner } from './About';
import { Search, Phone, Mail, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { resolveImageUrl } from '../components/ImageUpload';
import useSeo from '../hooks/useSeo';

export default function Products() {
  const { products, site_content } = useContent();
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => products.filter(p => p.name.toLowerCase().includes(query.toLowerCase())), [products, query]);

  return (
    <div>
      <PageBanner title={site_content?.page_products_title || 'Our Products'} />
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
            <p className="text-gray-600 text-sm">Showing {filtered.length} of {products.length} products</p>
            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Search products..." className="pl-9" value={query} onChange={e => setQuery(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {filtered.map(p => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="product-card block rounded-md">
                <div className="thumb"><img src={resolveImageUrl(p.image)} alt={p.name} loading="lazy" /></div>
                <h3>{p.name}</h3>
              </Link>
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-center py-10 text-gray-500">No products found.</p>
          )}
        </div>
      </section>
    </div>
  );
}

export function ProductDetail() {
  const { slug } = useParams();
  const { products } = useContent();
  const product = products.find(p => p.slug === slug);

  // Per-product SEO with auto fallback to name + description
  const seoTitle = product
    ? (product.seo_title || `${product.name} | Ess Bee Heaters Pvt. Ltd.`)
    : 'Product Not Found | Ess Bee Heaters';
  const seoDescription = product
    ? (product.seo_description
        || product.description
        || `Buy ${product.name.toLowerCase()} from Ess Bee Heaters Pvt. Ltd. — ISO 9001:2015 certified manufacturer of industrial heating elements in Delhi, India.`)
    : '';
  const seoKeywords = product
    ? (product.seo_keywords || `${product.name.toLowerCase()}, heating elements, industrial heater, Ess Bee Heaters, Delhi`)
    : '';
  const canonical = typeof window !== 'undefined'
    ? `${window.location.origin}/products/${slug}`
    : '';
  useSeo({
    title: seoTitle,
    description: seoDescription,
    keywords: seoKeywords,
    image: product?.image,
    url: canonical,
  });

  if (!product) {
    return (
      <div>
        <PageBanner title="Product Not Found" />
        <div className="max-w-7xl mx-auto px-4 py-14 text-center">
          <p className="text-gray-600 mb-4">The product you are looking for does not exist.</p>
          <Link to="/products" className="btn-brand">Back to Products</Link>
        </div>
      </div>
    );
  }
  const related = products.filter(p => p.slug !== product.slug).slice(0, 4);

  return (
    <div>
      {/* JSON-LD structured data for rich Google results */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        image: product.image,
        description: seoDescription,
        sku: product.slug,
        brand: { '@type': 'Brand', name: 'Ess Bee Heaters Pvt. Ltd.' },
        manufacturer: { '@type': 'Organization', name: 'Ess Bee Heaters Pvt. Ltd.' },
      }) }} />
      <PageBanner title={product.name} />
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-10">
          <div className="bg-[#fffaf0] border border-[#f4e6c6] rounded-lg p-6 flex items-center justify-center">
            <img src={resolveImageUrl(product.image)} alt={product.name} className="max-h-96 object-contain" />
          </div>
          <div>
            <h1 className="font-display text-2xl md:text-3xl uppercase text-gray-900 mb-2">{product.name}</h1>
            <div className="w-16 h-1 bg-[#f7941e] mb-4 rounded" />
            <p className="text-gray-600 leading-relaxed mb-4">
              Our {product.name.toLowerCase()} are engineered for high performance and long service life across industrial applications. Manufactured using premium-grade materials and rigorously tested in our in-house laboratory to meet international standards.
            </p>
            <ul className="space-y-2 mb-6">
              {['High efficiency heat transfer','Custom sizes and wattages available','Corrosion resistant construction','Long service life','ISO 9001:2015 certified quality'].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-700"><ArrowRight className="w-4 h-4 text-[#f7941e] mt-0.5" />{f}</li>
              ))}
            </ul>
            <div className="bg-[#fffaf0] border border-[#f4e6c6] rounded-lg p-5">
              <h4 className="font-display uppercase text-gray-900 mb-3">Get a Quote</h4>
              <div className="flex flex-wrap gap-3">
                <a href="tel:+919911565091" className="btn-brand"><Phone className="w-4 h-4" /> Call Now</a>
                <a href="mailto:ess_beeheat@yahoo.com" className="inline-flex items-center gap-2 border border-[#f7941e] text-[#e67e00] font-display px-5 py-2.5 rounded hover:bg-[#f7941e] hover:text-white transition-colors"><Mail className="w-4 h-4" /> Email Enquiry</a>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 mt-14">
          <h3 className="font-display text-2xl uppercase text-gray-900 mb-6">Related Products</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {related.map(p => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="product-card block rounded-md">
                <div className="thumb"><img src={resolveImageUrl(p.image)} alt={p.name} loading="lazy" /></div>
                <h3>{p.name}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
